import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import LaunchIcon from '@mui/icons-material/Launch'
import { AccessTime } from '@workwave-tidal/core/icons/AccessTime'
import { PestPacLocationDetailResponseDTO } from '@user/store/map/typings'
import { googleMapsOpenInMapsLink } from '@user/components/HereMap/utils/googleMapsLinks'
import EmailIcon from '@mui/icons-material/MailOutline'
import PhoneIcon from '@mui/icons-material/Phone'
import { KnockStatusChip } from '@user/features/MapView/KnockStatusChip'
import {
  formatKnockStatusCreatedInfo,
  hasViewableHistory,
} from '@user/utils/knockStatusFormatting'
import { ServiceHistoryTab } from './ServiceHistoryTab'
import {
  ClearKnockStatusButton,
  ClearKnockStatusConfirmDialog,
  useClearKnockStatus,
  useKnockStatusEditPermission,
} from '@user/features/MapView/ClearKnockStatus'
import { EditableKnockStatusReason } from '@user/features/MapView/KnockStatusModal/EditableKnockStatusReason'

const OVERVIEW_TAB = 0
const SERVICE_HISTORY_TAB = 1

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pestpac-tabpanel-${index}`}
      aria-labelledby={`pestpac-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

interface Props {
  detail: PestPacLocationDetailResponseDTO | null
  status: 'empty' | 'loading' | 'error' | 'success'
  onAddLead: () => void
  onKnockStatusClick: () => void
  onViewKnockStatusHistory?: () => void
  onRefresh?: () => void
  onOpenPestPacLocation?: (locationId: string) => void
  isSavingReason?: boolean
  onSaveReason?: (newReason: string | null) => Promise<boolean>
}

export function PestPacDetailContent({
  detail,
  status,
  onAddLead,
  onKnockStatusClick,
  onViewKnockStatusHistory,
  onRefresh,
  onOpenPestPacLocation,
  isSavingReason,
  onSaveReason,
}: Props): JSX.Element {
  const [tabValue, setTabValue] = useState(SERVICE_HISTORY_TAB)
  // Each account opens on Service History: a different account resets the tab,
  // while a refresh of the same one keeps the tab the user picked.
  const locationId = detail?.locationId
  const [shownLocationId, setShownLocationId] = useState(locationId)
  if (locationId !== shownLocationId) {
    setShownLocationId(locationId)
    setTabValue(SERVICE_HISTORY_TAB)
  }

  const [canEdit] = useKnockStatusEditPermission('KnockStatus')

  // Standalone (external) PestPac pin: backend resolves 'Unknown' -> 'KnockStatus' (SH-851).
  const {
    clearConfirmOpen,
    isClearing,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
  } = useClearKnockStatus({
    knockStatusLocationId: detail?.knockStatus?.knockStatusLocationId ?? null,
    variant: '',
    entityType: 'Unknown',
    onRefresh,
  })

  const renderTitle = (text: string) => (
    <Typography variant="h6" sx={{ color: '#1565C0' }} mb={'12px'}>
      {text}
    </Typography>
  )

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (status === 'error' || !detail) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load PestPac location details</Alert>
      </Box>
    )
  }

  const getPhoneNumber = (): string | null => {
    if (detail.mobile) return detail.mobile
    if (detail.phone) return detail.phone
    if (detail.altPhone) return detail.altPhone
    return null
  }

  const phoneNumber = getPhoneNumber()

  const email = detail.email

  const createdInfoText = formatKnockStatusCreatedInfo(
    detail.knockStatus?.createdTime,
    detail.knockStatus?.createdByName,
  )

  const knockStatusCount = detail.knockStatus?.knockStatusCount ?? 0
  const hasKnockStatus =
    Boolean(detail.knockStatus?.knockStatusName) && knockStatusCount > 0

  const handleEmailClick = () => {
    if (email) {
      window.open(`mailto:${email}`, '_blank')
    }
  }

  const handlePhoneClick = () => {
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`, '_blank')
    }
  }

  const canOpenPestPacLocation = Boolean(
    detail.locationId && onOpenPestPacLocation,
  )

  const handleOpenPestPacLocation = () => {
    if (detail.locationId && onOpenPestPacLocation) {
      onOpenPestPacLocation(detail.locationId)
    }
  }

  const handleAddressClick = () => {
    if (detail.formattedAddress) {
      window.open(
        googleMapsOpenInMapsLink(detail.formattedAddress),
        '_blank',
        'noopener,noreferrer',
      )
    }
  }

  return (
    <>
      <Box sx={{ marginLeft: 2, marginRight: 2, flexShrink: 0 }}>
        {/* Header: Service Location Name — opens the location in PestPac */}
        {canOpenPestPacLocation ? (
          <Link onClick={handleOpenPestPacLocation} sx={{ cursor: 'pointer' }}>
            {renderTitle(detail.serviceLocationName || '-')}
          </Link>
        ) : (
          renderTitle(detail.serviceLocationName || '-')
        )}

        {/* Address — opens the location in Google Maps */}
        {detail.formattedAddress ? (
          <Link onClick={handleAddressClick} sx={{ cursor: 'pointer' }}>
            <Typography
              component={'div'}
              variant="body2"
              sx={{
                color: '#1565C0',
                cursor: 'pointer',
                '&:hover': {
                  color: 'rgba(25, 112, 211, 0.9)',
                },
              }}
            >
              {detail.formattedAddress}
              <LaunchIcon sx={{ ml: 0.5, mb: 0.4, fontSize: '20px' }} />
            </Typography>
          </Link>
        ) : null}

        <Stack display="flex" gap={1.2} mt={1}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            sx={{
              mt: 2.4,
            }}
            onClick={onAddLead}
          >
            Add Lead
          </Button>
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="secondary"
            sx={{
              mt: 0,
              backgroundColor: '#D4E3F3',
            }}
            onClick={onKnockStatusClick}
          >
            Knock Status
          </Button>
        </Stack>

        {/* Knock Status Box - same style as Lead/Opp */}
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 400,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
              }}
            >
              Knock status
            </Typography>
            {hasKnockStatus && canEdit && (
              <ClearKnockStatusButton onClick={openClearConfirm} />
            )}
          </Box>
          <Box>
            {detail.knockStatus?.knockStatusName ? (
              <KnockStatusChip
                status={detail.knockStatus.knockStatusName}
                isDefault={
                  detail.knockStatus.knockStatusName === 'Do not knock'
                }
              />
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Box>

          <EditableKnockStatusReason
            reason={detail.knockStatus?.reason ?? null}
            canEdit={hasKnockStatus && Boolean(detail.knockStatus?.id)}
            isSaving={Boolean(isSavingReason)}
            onSave={onSaveReason ?? (async () => false)}
          />

          {createdInfoText && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.6,
                color: '#0E283FB2',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <AccessTime fontSize="small" />
              {'  '}
              {createdInfoText}
            </Typography>
          )}

          {hasViewableHistory(
            knockStatusCount,
            Boolean(detail.knockStatus?.knockStatusName),
          ) && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="text"
                size="small"
                onClick={onViewKnockStatusHistory}
                sx={{ color: '#1565C0' }}
              >
                View Knock Status History
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      <ClearKnockStatusConfirmDialog
        open={clearConfirmOpen}
        isClearing={isClearing}
        onCancel={closeClearConfirm}
        onConfirm={handleClearKnockStatus}
      />

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mx: 1.2,
          mt: '7px',
          flexShrink: 0,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="PestPac details tabs"
        >
          <Tab
            label="Overview"
            id="pestpac-tab-0"
            aria-controls="pestpac-tabpanel-0"
          />
          <Tab
            label="Service History"
            id="pestpac-tab-1"
            aria-controls="pestpac-tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Neutral surface behind the tab content, white cards on top */}
      <Box pt={2} pb={2} bgcolor={'#f9fafb'} sx={{ flexGrow: 1 }}>
        <TabPanel value={tabValue} index={OVERVIEW_TAB}>
          <Stack
            direction="column"
            sx={{
              paddingLeft: 2,
              paddingRight: 2,
            }}
          >
            {/* Location */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                }}
              >
                Location
              </Typography>
              {detail.locationCode ? (
                <Link
                  onClick={handleOpenPestPacLocation}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography
                    component={'div'}
                    variant="body2"
                    sx={{
                      color: '#1565C0',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'rgba(25, 112, 211, 0.9)',
                      },
                    }}
                  >
                    {detail.locationCode}
                    <LaunchIcon sx={{ ml: 0.5, mb: 0.4, fontSize: '20px' }} />
                  </Typography>
                </Link>
              ) : (
                <Typography variant="body2">-</Typography>
              )}
            </Paper>

            {/* Primary Contact Information */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1.5,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  mb: 0.5,
                }}
              >
                Primary Contact Information
              </Typography>

              {phoneNumber ? (
                <Button
                  onClick={handlePhoneClick}
                  variant="text"
                  startIcon={
                    <PhoneIcon sx={{ fontSize: 24, color: '#1565C0' }} />
                  }
                  sx={{ color: '#1565C0', width: 'fit-content' }}
                >
                  {phoneNumber}
                </Button>
              ) : null}
              {email ? (
                <Button
                  onClick={handleEmailClick}
                  variant="text"
                  startIcon={
                    <EmailIcon sx={{ fontSize: 24, color: '#1565C0' }} />
                  }
                  sx={{ color: '#1565C0', width: 'fit-content' }}
                >
                  {email}
                </Button>
              ) : null}
              {!phoneNumber && !email ? (
                <Typography variant="body2">-</Typography>
              ) : null}
            </Paper>
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={SERVICE_HISTORY_TAB}>
          <Box sx={{ px: 2 }}>
            <ServiceHistoryTab
              locationId={detail.locationId}
              tabActive={tabValue === SERVICE_HISTORY_TAB}
            />
          </Box>
        </TabPanel>
      </Box>
    </>
  )
}

export default PestPacDetailContent
